"use client";

import * as React from "react";
import { toast } from "sonner";
import { trpcReactQueryApi } from "~/trpc/react";
import {
  ProjectsSortBy,
  type UseProjectsDashboardParams,
  type UseProjectsDashboardReturn,
} from "./use-projects-dashboard.interface";

/**
 * Implementation of the Projects Dashboard domain hook.
 * Uses TRPC for server interactions and react-query for caching/invalidation.
 */
export function useProjectsDashboard(
  params: UseProjectsDashboardParams,
): UseProjectsDashboardReturn {
  const { autoLoad = false } = params;

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // State for sorting and running analyses
  const [sortBy, setSortBy] = React.useState<ProjectsSortBy>(ProjectsSortBy.LOWEST_PERCENT);
  const [runningAnalysisProjectIds, setRunningAnalysisProjectIds] = React.useState<string[]>([]);

  // Load projects query
  const projectsQuery = trpcReactQueryApi.main.projects.all.useQuery(undefined, {
    enabled: autoLoad,
  });

  // Derived sorted projects
  const sortedProjects = React.useMemo(() => {
    if (!projectsQuery.data) return [];
    const projects = [...projectsQuery.data];
    switch (sortBy) {
      case ProjectsSortBy.LOWEST_PERCENT:
        return projects.sort((a, b) => a.percentComplete - b.percentComplete || a.name.localeCompare(b.name));
      case ProjectsSortBy.HIGHEST_PERCENT:
        return projects.sort((a, b) => b.percentComplete - a.percentComplete || a.name.localeCompare(b.name));
      case ProjectsSortBy.LAST_ANALYZED:
        return projects.sort((a, b) => {
          const aDate = a.lastAnalysisAt?.getTime() ?? 0;
          const bDate = b.lastAnalysisAt?.getTime() ?? 0;
          return bDate - aDate || a.name.localeCompare(b.name);
        });
      case ProjectsSortBy.MOST_OPEN_TASKS:
        return projects.sort((a, b) => b.openTasksCount - a.openTasksCount || a.name.localeCompare(b.name));
      default:
        return projects;
    }
  }, [projectsQuery.data, sortBy]);

  // Mutations
  const createProjectMutation = trpcReactQueryApi.main.projects.create.useMutation({
    onSuccess: async () => {
      await utils.main.projects.all.invalidate();
      toast.success("Project created successfully");
    },
    onError: (error) => {
      toast.error(error.message ?? "Failed to create project");
    },
  });

  const deleteProjectMutation = trpcReactQueryApi.main.projects.delete.useMutation({
    onSuccess: async () => {
      await utils.main.projects.all.invalidate();
      toast.success("Project deleted successfully");
    },
    onError: (error) => {
      toast.error(error.message ?? "Failed to delete project");
    },
  });

  const runAnalysisMutation = trpcReactQueryApi.main.analysis.runAnalysis.useMutation({
    onSuccess: async (result) => {
      await utils.main.projects.all.invalidate();
      
      // Show appropriate toast based on results
      if (result.addedCount === 0) {
        toast.info("No new tasks found.");
      } else {
        toast.success(`Added ${result.addedCount} new tasks.`);
      }
    },
    onError: (error) => {
      toast.error(error.message ?? "Analysis failed");
    },
  });

  // Commands
  const loadProjects = React.useCallback(async () => {
    await projectsQuery.refetch();
  }, [projectsQuery]);

  const createProject = React.useCallback(
    async (name: string, baseUrl: string) => {
      await createProjectMutation.mutateAsync({ name, baseUrl });
    },
    [createProjectMutation],
  );

  const deleteProject = React.useCallback(
    async (projectId: string) => {
      await deleteProjectMutation.mutateAsync({ id: projectId });
    },
    [deleteProjectMutation],
  );

  const runAnalysis = React.useCallback(
    async (projectId: string) => {
      setRunningAnalysisProjectIds((prev) => [...prev, projectId]);
      try {
        await runAnalysisMutation.mutateAsync({ projectId });
      } finally {
        setRunningAnalysisProjectIds((prev) => prev.filter((id) => id !== projectId));
      }
    },
    [runAnalysisMutation],
  );

  // Mutations (setters)
  const setSortByMutation = React.useCallback((newSortBy: ProjectsSortBy) => {
    setSortBy(newSortBy);
  }, []);

  return {
    // State
    projects: projectsQuery.data ?? [],
    sortedProjects,
    sortBy,
    isLoading: projectsQuery.isLoading,
    error: projectsQuery.isError ? "Failed to load projects" : null,
    runningAnalysisProjectIds,

    // Mutations
    setSortBy: setSortByMutation,

    // Commands
    loadProjects,
    createProject,
    deleteProject,
    runAnalysis,
  };
}
